import { useModal } from "@/Contexts";
import { isCarrotAdmin } from "@/Utils/authUtils";
import {
  SmartIntegration,
  SmartIntegrationWithTranslation,
} from "@/Utils/smartInfrastructure";
import { smartCapabilitiesColumns } from "@/Utils/tableColumns/smartCapabilitiesColumns";
import { AlertLine } from "@/components/AlertLine/AlertLine";
import InfoPopup from "@/components/InfoPopup/InfoPopup";
import { Table } from "@/components/Table";
import { Button } from "@/components/button/index";
import { Card, CardComponentVariant } from "@/components/pageCards/card";
import { useAccessPoints } from "@/pages/infrastructure/manage/useAccessPoints";
import { EditSmartFunctionalityModal } from "@/pages/infrastructure/modals/editSmartFunctionalityModal";
import { SmartFunctionalityModal } from "@/pages/infrastructure/modals/smartFunctionalityModal";
import { Translate, useTrans } from "@/translations";

import { FC, useMemo } from "react";

import { ContainerDrawerProps } from "../containerDrawer";

export const ContainerSmartCapabilities: FC<ContainerDrawerProps> = ({
  accessParentId,
  containerId,
  depotId,
}) => {
  const { t } = useTrans();
  const { showModal } = useModal();

  const { getContainer } = useAccessPoints({ accessParentId });
  const container = getContainer(containerId, depotId);

  const smartIntegrations = useMemo(
    () =>
      JSON.parse(container?.smartIntegrations ?? "[]").map(
        (si: SmartIntegration) => ({
          ...si,
          capabilityTypeTranslation: t(
            `entities:smart-tech-${si.capabilityType}`,
          ),
        }),
      ) as SmartIntegrationWithTranslation[],
    [container, t],
  );

  const canAddSmartFunctionality = useMemo(
    () =>
      smartIntegrations.length < 2 &&
      !smartIntegrations
        .map((si: SmartIntegration) => si.capabilityType)
        .includes("integrated"),
    [smartIntegrations],
  );

  const openEditModal = () =>
    container &&
    showModal(
      <EditSmartFunctionalityModal {...{ accessParentId, container }} />,
    );

  const openSmartModal = () =>
    container &&
    showModal(<SmartFunctionalityModal {...{ accessParentId, container }} />);

  return (
    <Card
      {...{
        title: "drawerLabels:smartCapabilities",
      }}
      overflow="auto"
      variant={CardComponentVariant.DRAWER}
    >
      <div className="font-dm-sans mb-4 text-sm">
        {t("hints:manageSmartHardwareDescription")}
        <InfoPopup
          id={containerId}
          size={15}
          className="ml-1 inline-block align-baseline"
          width="350px"
        >
          <Translate i18nKey="hints:manageSmartHardwareDescriptionPopup">
            <p className="my-2" />
            <p className="my-2">
              {/* eslint-disable-next-line jsx-a11y/anchor-has-content */}
              <a
                href="mailto:support@carrot.tech"
                className="text-white underline"
              />
            </p>
            <p className="my-2" />
          </Translate>
        </InfoPopup>
      </div>
      <Table
        columns={smartCapabilitiesColumns}
        data={smartIntegrations}
        emptyViewType="noTechnologies"
        emptyViewClassName="bg-grey1 mt-0"
      />

      {!canAddSmartFunctionality && (
        <div className="my-4">
          <AlertLine
            type="warning"
            message="hints:manageSmartHardwareLimitReached"
            canClose={false}
            isStatic
          />
        </div>
      )}

      <div>
        {canAddSmartFunctionality && (
          <div className="float-right ml-4">
            <Button
              className=""
              label="actions:addNew"
              color="secondary"
              onClick={openSmartModal}
            />
          </div>
        )}

        {isCarrotAdmin() && smartIntegrations.length > 0 && (
          <div className="float-right">
            <Button
              label="actions:edit"
              color="secondary"
              onClick={openEditModal}
            />
          </div>
        )}
      </div>
    </Card>
  );
};
